import { FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";

import { RiInformationLine } from "react-icons/ri";

import {
  PayerTier,
  ActionType,
  Claim,
  ClaimAppeal,
  ClaimAppealLetter,
  ClaimPayer,
  FeeChange,
  RecordAppealInput,
} from "../../../../types/claims";
import { apiService } from "../../../../services/apiService";
import useNotifier from "../../../../hooks/useNotifier";
import CoreDatePicker from "../../../ui/core/CoreDatePicker";
import CoreSelect from "../../../ui/core/CoreSelect";
import CoreTextarea from "../../../ui/core/CoreTextarea";
import { formatDisplayDate } from "../../../helpers/dataHelper";
import { getFirstAppealLetter } from "./claimTimeline";
import {
  BeforeAfter,
  EditErrorAlert,
  EffectPanel,
  FieldMessage,
  NoneText,
  PdfDropzone,
  RuleItem,
  RulesList,
  SummaryRow,
} from "./record-payment/ClaimEditDialogParts";
import {
  ACTION_TYPE_LABELS,
  AppealError,
  AppealFieldErrors,
  BASELINE_FALLBACK_MESSAGES,
  CORRECT_RESUBMIT,
  MAX_CLAIM_DOCUMENT_MB,
  PAYER_TIER_LABELS,
  claimDocumentError,
  appealTypeFromLetter,
  hasCorrectResubmit,
  browserTimezone,
  defaultPayerTier as defaultPayerTierFor,
  formatFeeAmount,
  formatFeeMonth,
  formatFeePct,
  isBlockingAppealError,
  latestAppealDate,
  parseAppealError,
  payerLabel,
  payerOptions,
  toLocalIsoDate,
  validateAppealForm,
} from "./claimAppeals";

interface RecordAppealDialogProps {
  open: boolean;
  claim: Claim;
  appealLetters: ClaimAppealLetter[];
  /** The actions already recorded: a claim holds one correct and resubmit at most. */
  appeals?: ClaimAppeal[];
  /** The claim's payer levels from the claim detail (REV-2621). */
  payers?: ClaimPayer[];
  onClose: () => void;
  /** Called with the saved action, or null when the API says it already exists
   * (duplicate_appeal, already_resubmitted), so the caller refreshes the
   * timeline either way. `actionType` is the type that was submitted. */
  onRecorded: (appeal: ClaimAppeal | null, actionType: ActionType) => void;
  /** Given, a saved correct and resubmit can go straight on to linking the
   * corrected claim (REV-2629): the dialog closes and this opens the link step.
   * The API links only a saved action, so it can't be part of the save. */
  onLinkCorrectedClaim?: () => void;
}

// "saved" only follows a save made without a fee preview, to show the fee the save returned
type Step = "form" | "confirm" | "saved";

const TITLES: Record<Step, string> = {
  form: "Record action",
  confirm: "Confirm action",
  saved: "Action recorded",
};

const ALL_ACTION_TYPES = Object.keys(ACTION_TYPE_LABELS) as ActionType[];

const FeeEffect: FC<{ fee: FeeChange }> = ({ fee }) => {
  if (!fee.changed) {
    return (
      <Typography sx={{ fontSize: "0.8125rem" }}>
        No change to the success fee ({formatFeeAmount(fee.successFee.after)}).
      </Typography>
    );
  }
  const date = (value: string | null) =>
    value ? formatDisplayDate(value) : "—";
  return (
    <Box>
      <SummaryRow label="Success fee">
        <BeforeAfter
          before={formatFeeAmount(fee.successFee.before)}
          after={formatFeeAmount(fee.successFee.after)}
        />
      </SummaryRow>
      <SummaryRow label="Billing appeal date">
        <BeforeAfter
          before={date(fee.billingAppealDate.before)}
          after={date(fee.billingAppealDate.after)}
        />
      </SummaryRow>
      <SummaryRow label="Recovery rate">
        <BeforeAfter
          before={formatFeePct(fee.recoveryPct.before)}
          after={formatFeePct(fee.recoveryPct.after)}
        />
      </SummaryRow>
      <SummaryRow label="Paid at appeal">
        <BeforeAfter
          before={formatFeeAmount(fee.paidAtAppeal.before)}
          after={formatFeeAmount(fee.paidAtAppeal.after)}
        />
      </SummaryRow>
      {fee.restatedMonths.length > 0 && (
        <Typography
          sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}
        >
          Restates the success fee already booked for{" "}
          {fee.restatedMonths.map(formatFeeMonth).join(", ")}.
        </Typography>
      )}
    </Box>
  );
};

/**
 * Admin-only: records an action (appeal, DOFR appeal, correct and resubmit) on
 * the claim in two steps. The form explains the
 * rules as it is filled in; the summary shows the success fee before and after
 * (POST appeals/preview/, which writes nothing). Only confirming the summary saves.
 */
const RecordAppealDialog: FC<RecordAppealDialogProps> = ({
  open,
  claim,
  appealLetters,
  appeals,
  payers,
  onClose,
  onRecorded,
  onLinkCorrectedClaim,
}) => {
  const theme = useTheme();
  const notifier = useNotifier();

  const firstLetter = getFirstAppealLetter(appealLetters);
  // A letter fixes Appeal vs DOFR Appeal; a correct and resubmit is always the
  // admin's choice. A letter whose type the API doesn't map fixes nothing.
  const letterType = appealTypeFromLetter(firstLetter);
  const alreadyResubmitted = hasCorrectResubmit(appeals);
  const actionTypes = ALL_ACTION_TYPES.filter((type) =>
    type === CORRECT_RESUBMIT
      ? !alreadyResubmitted
      : !letterType || type === letterType,
  );
  // Nothing left to choose: the letter's type, once the claim was resubmitted
  const fixedType = actionTypes.length === 1 ? actionTypes[0] : null;
  const actionTypeOptions = actionTypes.map((type) => ({
    value: type,
    label: ACTION_TYPE_LABELS[type],
  }));
  const letterMinDate = firstLetter
    ? toLocalIsoDate(firstLetter.generatedAt)
    : null;
  const today = latestAppealDate();
  const defaultPayerTier = defaultPayerTierFor(claim.status, payers);

  const [step, setStep] = useState<Step>("form");
  const [actionOn, setActionOn] = useState(today);
  const [payerTier, setPayerTier] = useState<PayerTier>(defaultPayerTier);
  const [chosenType, setChosenType] = useState<ActionType | "">("");
  const [comment, setComment] = useState("");
  const [document, setDocument] = useState<File | null>(null);
  // Optional: after saving a correct and resubmit, go on to link the corrected claim
  const [linkNext, setLinkNext] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<AppealFieldErrors>({});
  // A validation_error the form can't pin on a field
  const [formError, setFormError] = useState<string | null>(null);
  const [fee, setFee] = useState<FeeChange | null>(null);
  const [confirmError, setConfirmError] = useState<AppealError | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Start fresh each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setStep("form");
    setActionOn(latestAppealDate());
    setPayerTier(defaultPayerTierFor(claim.status, payers));
    setChosenType("");
    setComment("");
    setDocument(null);
    setLinkNext(true);
    setFieldErrors({});
    setFormError(null);
    setFee(null);
    setConfirmError(null);
    setIsPreviewing(false);
    setIsSaving(false);
  }, [open, claim.status, payers]);

  const clearFieldError = (field: keyof AppealFieldErrors) =>
    setFieldErrors((errors) => {
      if (!errors[field]) return errors;
      const next = { ...errors };
      delete next[field];
      return next;
    });

  // With a letter its type is pre-selected; a correct and resubmit only by choice
  const actionType: ActionType | "" =
    fixedType ??
    (actionTypes.includes(chosenType as ActionType) ? chosenType : null) ??
    letterType ??
    "";
  const isResubmit = actionType === CORRECT_RESUBMIT;
  const willLinkNext = isResubmit && linkNext && !!onLinkCorrectedClaim;
  // A correct and resubmit isn't held to the letter's date
  const minDate = isResubmit ? null : letterMinDate;

  const buildInput = (): RecordAppealInput => ({
    actionOn,
    payerTier,
    actionType: actionType as ActionType,
    comment: comment.trim(),
    timezone: browserTimezone(),
  });

  const handleFiles = (files: File[]) => {
    const [file] = files;
    if (!file) return;
    const error = claimDocumentError(file);
    if (error) {
      setFieldErrors((errors) => ({ ...errors, document: error }));
      return;
    }
    setDocument(file);
    clearFieldError("document");
  };

  const handleReview = async () => {
    const errors = validateAppealForm({
      actionOn,
      actionType,
      today,
      minDate: letterMinDate,
    });
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    setFormError(null);
    setIsPreviewing(true);
    setFee(null);
    // confirmError stays up during a retry so its button can show progress
    try {
      const response = await apiService.previewClaimAppeal(
        claim.id,
        buildInput(),
      );
      if (response.success && response.data) {
        setConfirmError(null);
        setFee(response.data);
        setStep("confirm");
        return;
      }
      const error = parseAppealError(response);
      if (error.code === "validation_error") {
        // Retrying the same input can't help, so it goes back to the form
        showValidationError(error);
        return;
      }
      // claim_invoiced: the summary shows why it can't be saved. Any other
      // failure still lets the admin save, and the save returns the fee.
      setConfirmError(error);
      setStep("confirm");
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setConfirmError(null);
    try {
      const response = await apiService.recordClaimAppeal(
        claim.id,
        buildInput(),
        document,
      );
      if (response.success && response.data) {
        notifier.showSuccess("Action recorded.");
        onRecorded(response.data.appeal, actionType as ActionType);
        // Saved without a preview: show the fee the save returned before closing
        if (!fee && response.data.fee) {
          setFee(response.data.fee);
          setStep("saved");
          return;
        }
        finish();
        return;
      }
      const error = parseAppealError(response);
      if (error.code === "validation_error") {
        showValidationError(error);
        return;
      }
      setConfirmError(error);
      // Someone (or a double submit) already saved it: show it on the timeline
      if (
        error.code === "duplicate_appeal" ||
        error.code === "already_resubmitted"
      )
        onRecorded(null, actionType as ActionType);
    } finally {
      setIsSaving(false);
    }
  };

  // After a save: on to linking the corrected claim, when asked for
  const finish = () => {
    if (willLinkNext && onLinkCorrectedClaim) onLinkCorrectedClaim();
    else onClose();
  };

  const showValidationError = (error: AppealError) => {
    setConfirmError(null);
    if (Object.keys(error.fieldErrors).length) {
      setFieldErrors(error.fieldErrors);
    } else {
      setFormError(error.message);
    }
    setStep("form");
  };

  // A late preview or save response would land on a closed dialog
  const handleClose = () => {
    if (isSaving || isPreviewing) return;
    onClose();
  };

  const isBlocked = !!confirmError && isBlockingAppealError(confirmError.code);
  // The fee preview is informative only: a failed one (other than a blocking
  // error) doesn't stop the save, which returns the fee itself
  const canSave =
    step === "confirm" && !isBlocked && !isSaving && !isPreviewing;

  const dateRule = minDate
    ? `Not in the future, and not before the first appeal letter (${formatDisplayDate(minDate)}).`
    : "Not in the future.";

  const renderForm = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2 }}>
      {formError && <EditErrorAlert message={formError} />}

      {fixedType ? (
        <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
          Action type: <b>{ACTION_TYPE_LABELS[fixedType]}</b>, from the claim's
          appeal letter. The claim is already corrected and resubmitted.
        </Typography>
      ) : (
        <Box>
          <CoreSelect
            label="Action type"
            variant="outlined"
            value={actionType}
            options={actionTypeOptions}
            displayEmpty
            placeholder="Choose the action"
            onChange={(e) => {
              setChosenType(e.target.value as ActionType);
              clearFieldError("actionType");
              // The date floor changes with the type
              clearFieldError("actionOn");
            }}
            disabled={isPreviewing}
            inputProps={{ "aria-label": "Action type" }}
          />
          <FieldMessage error={fieldErrors.actionType}>
            {letterType
              ? `${ACTION_TYPE_LABELS[letterType]} comes from the claim's appeal letter.`
              : alreadyResubmitted
                ? "The claim is already corrected and resubmitted."
                : "Choose the action."}
          </FieldMessage>
        </Box>
      )}

      <RulesList label="Action rules">
        <RuleItem>
          You'll see a summary before anything is saved. Saved actions can't be
          edited or removed.
        </RuleItem>
      </RulesList>

      <Box>
        <CoreDatePicker
          label="Action date"
          value={actionOn}
          onChange={(value) => {
            setActionOn(value);
            clearFieldError("actionOn");
          }}
          disableFuture
          minDate={minDate ? dayjs(minDate) : undefined}
          maxDate={dayjs(today)}
          disabled={isPreviewing}
        />
        <FieldMessage error={fieldErrors.actionOn}>{dateRule}</FieldMessage>
      </Box>

      {!isResubmit && (
        <Box>
          <CoreSelect
            label="Payer"
            variant="outlined"
            value={payerTier}
            options={payerOptions(payers)}
            onChange={(e) => {
              setPayerTier(e.target.value as PayerTier);
              clearFieldError("payerTier");
            }}
            disabled={isPreviewing}
            inputProps={{ "aria-label": "Payer" }}
          />
          <FieldMessage error={fieldErrors.payerTier}>
            {payerTier === defaultPayerTier
              ? "Pre-selected from the claim's status."
              : `The claim's status points at the ${PAYER_TIER_LABELS[defaultPayerTier].toLowerCase()}.`}
          </FieldMessage>
        </Box>
      )}

      <Box>
        <PdfDropzone
          label="Document (optional)"
          file={document}
          onFiles={handleFiles}
          onRemove={() => setDocument(null)}
          disabled={isPreviewing}
          hasError={!!fieldErrors.document}
          inputTestId="appeal-document-input"
        />
        <FieldMessage error={fieldErrors.document}>
          One PDF, up to {MAX_CLAIM_DOCUMENT_MB} MB: our letter, a fax or
          submission confirmation, or the payer's response.
        </FieldMessage>
      </Box>

      <Box>
        <CoreTextarea
          title="Comment (optional)"
          placeholder="What was sent, how, and anything the team should know"
          value={comment}
          minRows={3}
          maxRows={6}
          disabled={isPreviewing}
          onChange={(e) => {
            setComment(e.target.value);
            clearFieldError("comment");
          }}
          aria-label="Comment"
        />
        <FieldMessage error={fieldErrors.comment}>
          Without one, the claim log reads "Marked as{" "}
          {isResubmit ? "corrected and resubmitted" : "appealed"}".
        </FieldMessage>
      </Box>

      {isResubmit && onLinkCorrectedClaim && (
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={linkNext}
                disabled={isPreviewing}
                onChange={(e) => setLinkNext(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.8125rem" }}>
                Link the corrected claim after saving
              </Typography>
            }
          />
          <FieldMessage>
            Optional. Its payments then count toward this action. You can also
            link it later from the claim timeline.
          </FieldMessage>
        </Box>
      )}
    </Box>
  );

  const renderConfirm = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      {!isBlocked && (
        <Alert
          severity="warning"
          icon={<RiInformationLine size={20} />}
          sx={{ maxWidth: "100%" }}
        >
          This can't be edited or removed.
        </Alert>
      )}

      <Box>
        <SummaryRow label="Action date">
          {formatDisplayDate(actionOn)}
        </SummaryRow>
        {!isResubmit && (
          <SummaryRow label="Payer">{payerLabel(payerTier, payers)}</SummaryRow>
        )}
        {actionType && (
          <SummaryRow label="Action type">
            {ACTION_TYPE_LABELS[actionType]}
            {letterType && !isResubmit ? " (from the appeal letter)" : ""}
          </SummaryRow>
        )}
        <SummaryRow label="Comment">
          {comment.trim() || <NoneText />}
        </SummaryRow>
        <SummaryRow label="Document">
          {document?.name || <NoneText />}
        </SummaryRow>
        {isResubmit && onLinkCorrectedClaim && (
          <SummaryRow label="Corrected claim">
            {willLinkNext ? "Linked next, after saving" : <NoneText />}
          </SummaryRow>
        )}
      </Box>

      <EffectPanel title="Effect on the success fee">
        {fee ? (
          <FeeEffect fee={fee} />
        ) : (
          <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
            The fee effect couldn't be computed.
            {!isBlocked && " You can still save; it's shown once saved."}
          </Typography>
        )}
      </EffectPanel>

      {fee?.baselineFallback && (
        <Alert severity="warning" sx={{ maxWidth: "100%" }}>
          {BASELINE_FALLBACK_MESSAGES[fee.baselineFallback]}
        </Alert>
      )}

      {confirmError && (
        <EditErrorAlert
          message={confirmError.message}
          onRetry={!isBlocked && !fee ? handleReview : undefined}
          isRetrying={isPreviewing}
        />
      )}
    </Box>
  );

  const renderSaved = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      <Alert severity="success" sx={{ maxWidth: "100%" }}>
        Action recorded.
      </Alert>
      <EffectPanel title="Effect on the success fee">
        {fee && <FeeEffect fee={fee} />}
      </EffectPanel>
      {fee?.baselineFallback && (
        <Alert severity="warning" sx={{ maxWidth: "100%" }}>
          {BASELINE_FALLBACK_MESSAGES[fee.baselineFallback]}
        </Alert>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: "19px",
        }}
      >
        {TITLES[step]}
        <Typography
          component="div"
          sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
        >
          Claim {claim.claimNumber}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {step === "form" && renderForm()}
        {step === "confirm" && renderConfirm()}
        {step === "saved" && renderSaved()}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {step === "form" ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isPreviewing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReview}
              variant="contained"
              loading={isPreviewing}
            >
              Review
            </Button>
          </>
        ) : step === "saved" ? (
          <Button onClick={finish} variant="contained">
            {willLinkNext ? "Next: link the corrected claim" : "Done"}
          </Button>
        ) : (
          <>
            <Button
              onClick={() => {
                setStep("form");
                setConfirmError(null);
              }}
              variant="outlined"
              disabled={isSaving || isPreviewing}
            >
              Back
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!canSave}
              loading={isSaving}
            >
              Confirm and save
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RecordAppealDialog;
